import React, { useState, useContext, useMemo } from "react";
import {
    Dropdown,
    Button,
    OverlayTrigger,
    Tooltip,
    Spinner as CommonSpinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { gql, useQuery } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import AddPartOffCanvasWizard from "../../components/Wizards/AddPartOffCanvasWizard";
import "../../starek.css";
import DataTable from "react-data-table-component";
import moment from "moment";
import { deletePart } from "../../services/RepositoryService";

const Parts = () => {
    const starekContext = useContext(StarekContext);

    const GET_PARTS_LIST_FOR_PLANT = gql`
        query GetParts($plantId: Int!) {
            parts(where: { plant_id: { equals: $plantId } }) {
                id
                name
                description
                created_at
            }
        }
    `;

    const { data, loading, refetch } = useQuery(GET_PARTS_LIST_FOR_PLANT, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
    });

    const [showAddPartDrawer, setShowAddPartDrawer] = useState(false);
    const handleAddPartDrawerClose = () => setShowAddPartDrawer(false);
    const handleAddPartDrawerShow = () => setShowAddPartDrawer(true);
    const [filterText, setFilterText] = useState("");
    const [showColumns, setShowColumns] = useState([true, true, false]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [editingItem, setEditingItem] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const isAnySelected = selectedIds.size > 0;
    const isMultiSelected = selectedIds.size > 1;
    const isSingleSelected = selectedIds.size === 1;

    const parts = data?.parts ?? [];

    const selectedItems = useMemo(() => {
        if (!isAnySelected) return [];
        const ids = new Set(selectedIds);
        return parts.filter((p) => ids.has(p.id));
    }, [selectedIds, parts, isAnySelected]);

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return parts;
        return parts.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q)
        );
    }, [parts, filterText]);

    const toggleColumnVisibility = (index) => {
        setShowColumns((prev) => {
            const next = [...prev];
            next[index] = !next[index];
            return next;
        });
    };

    const onToggleRow = (row) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(row.id)) next.delete(row.id);
            else next.add(row.id);
            return next;
        });
    };

    const onToggleAllVisible = (checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                filteredItems.forEach((row) => next.add(row.id));
            } else {
                filteredItems.forEach((row) => next.delete(row.id));
            }
            return next;
        });
    };

    const handleBulkDelete = async () => {
        setIsDeleting(true);
        setDeleteDone(0);
        try {
            // delete
            const total = selectedIds.size;
            for (const id of selectedIds) {
                await deletePart(starekContext.lastUsedPlant.id, id);
                setDeleteDone((n) => n + 1);
            }
            setSelectedIds(new Set());
            setEditingItem(null);
            await refetch();
        } catch (error) {
            starekContext.showError(error);
        } finally {
            setIsDeleting(false);
            setDeleteDone(0);
        }
    };

    const handleEditSelected = () => {
        if (!isSingleSelected) return;
        const onlyItem = selectedItems[0];
        setEditingItem(onlyItem);
        handleAddPartDrawerShow();
    };

    const columns = [
        {
            name: (
                <input
                    type="checkbox"
                    aria-label="Select all"
                    onChange={(e) => onToggleAllVisible(e.target.checked)}
                    checked={
                        filteredItems.length > 0 &&
                        filteredItems.every((row) => selectedIds.has(row.id))
                    }
                    indeterminate={
                        filteredItems.some((row) => selectedIds.has(row.id)) &&
                        !filteredItems.every((row) => selectedIds.has(row.id))
                    }
                    ref={(el) => {
                        if (!el) return;
                        const some = filteredItems.some((r) =>
                            selectedIds.has(r.id)
                        );
                        const all =
                            filteredItems.length > 0 &&
                            filteredItems.every((r) => selectedIds.has(r.id));
                        el.indeterminate = some && !all;
                    }}
                />
            ),
            width: "60px",
            center: true,
            sortable: false,
            cell: (row) => (
                <input
                    type="checkbox"
                    checked={selectedIds.has(row.id)}
                    onChange={() => onToggleRow(row)}
                    aria-label={`Select ${row.name}`}
                />
            ),
            ignoreRowClick: true,
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                <div className="d-flex align-items-center">
                    <span className="w-30px h-30px me-2 d-flex align-items-center justify-content-center rounded-circle border bg-color-f2f2f2">
                        <i className="fa fa-image"></i>
                    </span>
                    <span>{row.name}</span>
                </div>
            ),
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
        },
        {
            name: "Created At",
            selector: (row) =>
                moment.utc(row.created_at).format("YYYY-MM-DD HH:mm:ss"),
            sortable: true,
        },
    ];

    const visibleColumns = columns.filter((_, index) => {
        if (index === 0) return true;
        return showColumns[index - 1];
    });

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
    };

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-hdd fa-10x text-success"></i>
            </div>
            <Spinner />
        </div>
    ) : (
        <div className="container-fluid">
            {parts && (
                <>
                    <div className="d-flex align-items-center justify-content-between mb-3 col-12">
                        <div className="d-flex align-items-center col-8">
                            <input
                                type="text"
                                className="border rounded p-2"
                                style={{ width: "60%" }}
                                placeholder="Search"
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                            <Dropdown
                                className="mx-3"
                                show={dropdownOpen}
                                onToggle={() => setDropdownOpen(!dropdownOpen)}
                            >
                                <Dropdown.Toggle
                                    variant="outline-success"
                                    id="dropdown-basic"
                                >
                                    <i className="fa fa-filter me-2"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {["Name", "Description", "Created At"].map(
                                        (columnName, i) => (
                                            <Dropdown.Item key={columnName}>
                                                <label className="d-flex align-items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={showColumns[i]}
                                                        onChange={() =>
                                                            toggleColumnVisibility(
                                                                i
                                                            )
                                                        }
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        className="me-2"
                                                    />
                                                    {columnName}
                                                </label>
                                            </Dropdown.Item>
                                        )
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        <div className="d-flex align-items-center justify-content-end col-4">
                            <div className="d-flex align-items-center tw-mr-2">
                                {isMultiSelected ? (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id="only-single-selection-tooltip">
                                                Only single selection can be
                                                deleted
                                            </Tooltip>
                                        }
                                    >
                                        <span className="ms-2 d-inline-block">
                                            <Button
                                                variant="secondary"
                                                disabled
                                            >
                                                Edit Part
                                            </Button>
                                        </span>
                                    </OverlayTrigger>
                                ) : (
                                    <Button
                                        className="ms-2"
                                        variant="outline-primary"
                                        onClick={handleEditSelected}
                                        disabled={
                                            !isSingleSelected ||
                                            isDeleting ||
                                            !isAnySelected
                                        }
                                    >
                                        Edit Part
                                    </Button>
                                )}
                                <Button
                                    className="ms-2"
                                    variant="danger"
                                    onClick={handleBulkDelete}
                                    disabled={isDeleting || !isAnySelected}
                                    aria-live="polite"
                                >
                                    {isDeleting ? (
                                        <>
                                            <CommonSpinner
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Deleting {deleteDone}/
                                            {selectedIds.size}
                                        </>
                                    ) : (
                                        "Delete Part"
                                    )}
                                </Button>
                            </div>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setEditingItem(null);
                                    handleAddPartDrawerShow();
                                }}
                                disabled={isDeleting}
                            >
                                Add Part
                            </Button>

                            {/* Drawer */}
                            <AddPartOffCanvasWizard
                                show={showAddPartDrawer}
                                closeOffcanvas={(itemModified, itemDeleted) => {
                                    setShowAddPartDrawer(false);
                                    if (itemModified || itemDeleted) {
                                        setSelectedIds(new Set());
                                        setEditingItem(null);
                                        refetch();
                                    }
                                }}
                                handleClose={handleAddPartDrawerClose}
                                starekContext={starekContext}
                                item={editingItem || null}
                            />
                        </div>
                    </div>

                    <DataTable
                        columns={visibleColumns}
                        data={filteredItems}
                        pagination
                        highlightOnHover
                        striped
                        responsive
                        defaultSortField="created_at"
                        defaultSortAsc={false}
                        customStyles={customStyles}
                    />
                </>
            )}
        </div>
    );
};

export { Parts };
